import React from 'react';
import { filterDOMProps } from '../../utils/domUtils';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'rectangular' | 'circular';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'rectangular',
  width,
  height,
  animation = 'pulse',
  className = '',
  style,
  ...props
}) => {
  const getVariantClasses = () => {
    switch (variant) {
      case 'text':
        return 'rounded';
      case 'circular':
        return 'rounded-full';
      default: // rectangular
        return 'rounded-md';
    }
  };

  const getAnimationClasses = () => {
    switch (animation) {
      case 'wave':
        return 'animate-pulse';
      case 'none':
        return '';
      default: // pulse
        return 'animate-pulse';
    }
  };

  const skeletonClasses = `
    bg-slate-200/80
    ${getVariantClasses()}
    ${getAnimationClasses()}
    ${className}
  `.trim();

  const defaultHeight = variant === 'text' ? '1em' : '1rem';
  const defaultWidth = variant === 'circular' ? height || defaultHeight : '100%';

  const skeletonStyle = {
    width: width || defaultWidth,
    height: height || defaultHeight,
  };

  const validProps = filterDOMProps(props);

  return <div className={skeletonClasses} style={skeletonStyle} {...validProps} />;
};

// Skeleton components spécialisés
export const SkeletonText: React.FC<Omit<SkeletonProps, 'variant'>> = (props) => (
  <Skeleton variant="text" {...props} />
);

export const SkeletonRect: React.FC<Omit<SkeletonProps, 'variant'>> = (props) => (
  <Skeleton variant="rectangular" {...props} />
);

export const SkeletonCircle: React.FC<Omit<SkeletonProps, 'variant'>> = (props) => (
  <Skeleton variant="circular" {...props} />
);

// Skeleton pour les cartes
export const SkeletonCard: React.FC = () => (
  <div className="p-4 border border-slate-200 rounded-lg">
    <div className="flex items-center space-x-4">
      <SkeletonCircle width="3rem" height="3rem" />
      <div className="flex-1 space-y-2">
        <SkeletonText width="60%" height="1.2em" />
        <SkeletonText width="40%" height="1em" />
      </div>
    </div>
    <div className="mt-4 space-y-2">
      <SkeletonRect height="1em" />
      <SkeletonRect height="1em" width="80%" />
    </div>
  </div>
);

// Skeleton pour les listes
export const SkeletonList: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <div className="space-y-3">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="flex items-center space-x-4">
        <SkeletonCircle width="2.5rem" height="2.5rem" />
        <div className="flex-1 space-y-2">
          <SkeletonText width="70%" height="1.1em" />
          <SkeletonText width="50%" height="0.9em" />
        </div>
      </div>
    ))}
  </div>
);

// Skeleton pour les tableaux de bord
export const SkeletonTable: React.FC<{ rows?: number; cols?: number }> = ({
  rows = 5,
  cols = 4,
}) => (
  <div className="space-y-3">
    {/* Header */}
    <div className="flex space-x-4 pb-2 border-b border-slate-200">
      {Array.from({ length: cols }, (_, i) => (
        <SkeletonText key={i} width="100%" height="1.2em" />
      ))}
    </div>
    {/* Rows */}
    {Array.from({ length: rows }, (_, rowIndex) => (
      <div key={rowIndex} className="flex space-x-4 py-2">
        {Array.from({ length: cols }, (_, colIndex) => (
          <SkeletonText key={colIndex} width="100%" height="1em" />
        ))}
      </div>
    ))}
  </div>
);

// Skeleton pour le dashboard doctor - file d'attente
export const SkeletonDoctorQueue: React.FC = () => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <SkeletonText width="200px" height="1.5em" />
      <SkeletonRect width="120px" height="2em" />
    </div>
    <div className="grid gap-3">
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="p-4 border border-slate-200 rounded-lg card-interactive">
          <div className="flex items-center space-x-3">
            <SkeletonCircle width="2.5rem" height="2.5rem" />
            <div className="flex-1 space-y-2">
              <SkeletonText width="60%" height="1.1em" />
              <SkeletonText width="40%" height="0.9em" />
            </div>
            <SkeletonRect width="80px" height="1.5em" />
          </div>
          <div className="mt-3 flex space-x-2">
            <SkeletonRect width="100px" height="2em" />
            <SkeletonRect width="120px" height="2em" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Skeleton pour le dashboard doctor - timeline
export const SkeletonDoctorTimeline: React.FC = () => (
  <div className="space-y-4">
    <SkeletonText width="180px" height="1.5em" />
    <div className="space-y-4">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="flex space-x-4">
          <div className="flex flex-col items-center">
            <SkeletonCircle width="1rem" height="1rem" />
            <SkeletonRect width="2px" height="60px" className="mt-2" />
          </div>
          <div className="flex-1 pb-8">
            <div className="flex items-center space-x-3 mb-2">
              <SkeletonText width="120px" height="1em" />
              <SkeletonText width="80px" height="0.9em" />
            </div>
            <SkeletonRect height="3em" className="mb-2" />
            <div className="flex space-x-2">
              <SkeletonRect width="90px" height="1.5em" />
              <SkeletonRect width="110px" height="1.5em" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Skeleton pour le dashboard doctor - panel d'alertes
export const SkeletonDoctorAlerts: React.FC = () => (
  <div className="space-y-4">
    <SkeletonText width="150px" height="1.5em" />
    <div className="space-y-3">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <SkeletonCircle width="1.5rem" height="1.5rem" />
            <div className="flex-1 space-y-2">
              <SkeletonText width="80%" height="1em" />
              <SkeletonText width="60%" height="0.9em" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Skeleton pour le dashboard patient - carte de prochaine visite
export const SkeletonPatientVisitCard: React.FC = () => (
  <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
    <div className="flex items-center space-x-4 mb-4">
      <SkeletonCircle width="3rem" height="3rem" />
      <div className="space-y-2">
        <SkeletonText width="200px" height="1.2em" />
        <SkeletonText width="150px" height="1em" />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4 mb-4">
      <div className="space-y-2">
        <SkeletonText width="100px" height="0.9em" />
        <SkeletonText width="120px" height="1em" />
      </div>
      <div className="space-y-2">
        <SkeletonText width="100px" height="0.9em" />
        <SkeletonText width="120px" height="1em" />
      </div>
    </div>
    <SkeletonRect height="2.5em" />
  </div>
);

// Skeleton pour le dashboard patient - checklist de préparation
export const SkeletonPatientChecklist: React.FC = () => (
  <div className="space-y-4">
    <SkeletonText width="180px" height="1.5em" />
    <div className="space-y-3">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="flex items-center space-x-3 p-3 border border-slate-200 rounded-lg">
          <SkeletonCircle width="1.5rem" height="1.5rem" />
          <div className="flex-1">
            <SkeletonText width="70%" height="1em" />
          </div>
          <SkeletonRect width="60px" height="1.5em" />
        </div>
      ))}
    </div>
  </div>
);

// Skeleton pour le dashboard patient - historique des consultations
export const SkeletonPatientHistory: React.FC = () => (
  <div className="space-y-4">
    <SkeletonText width="160px" height="1.5em" />
    <div className="space-y-3">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="p-4 border border-slate-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <SkeletonText width="140px" height="1.1em" />
            <SkeletonText width="100px" height="0.9em" />
          </div>
          <div className="space-y-2">
            <SkeletonText width="90%" height="1em" />
            <SkeletonText width="60%" height="1em" />
          </div>
          <div className="mt-3 flex space-x-2">
            <SkeletonRect width="100px" height="2em" />
            <SkeletonRect width="120px" height="2em" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Skeleton pour les métriques du dashboard
export const SkeletonDashboardMetrics: React.FC = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    {Array.from({ length: 4 }, (_, i) => (
      <div key={i} className="p-4 bg-white border border-slate-200 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <SkeletonText width="120px" height="1em" />
          <SkeletonCircle width="2rem" height="2rem" />
        </div>
        <SkeletonText width="80px" height="1.8em" className="mb-2" />
        <SkeletonText width="100px" height="0.9em" />
      </div>
    ))}
  </div>
);

// Skeleton pour la salle d'attente
export const SkeletonWaitingRoom: React.FC = () => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <SkeletonText width="180px" height="1.5em" />
      <div className="flex space-x-2">
        <SkeletonRect width="100px" height="2em" />
        <SkeletonRect width="120px" height="2em" />
      </div>
    </div>
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-slate-200">
        <div className="flex space-x-4">
          <SkeletonText width="80px" height="1em" />
          <SkeletonText width="100px" height="1em" />
          <SkeletonText width="90px" height="1em" />
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <SkeletonCircle width="2rem" height="2rem" />
                <div>
                  <SkeletonText width="120px" height="1em" className="mb-1" />
                  <SkeletonText width="80px" height="0.9em" />
                </div>
              </div>
              <div className="text-right">
                <SkeletonText width="60px" height="1em" className="mb-1" />
                <SkeletonText width="50px" height="0.9em" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);
